import { useState, useCallback, useRef } from "react";
import {
  fetchDrafts,
  saveDraft,
  deleteDraft,
} from "../services/studentActivityService";

/**
 * Drafts management hook with auto-save functionality
 */
const useDrafts = () => {
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  const hasFetched = useRef(false);
  const autoSaveTimeout = useRef(null);

  /**
   * Load all drafts
   */
  const loadDrafts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const data = await fetchDrafts();
      setDrafts(data);

      hasFetched.current = true;
    } catch (err) {
      console.error("Error loading drafts:", err);
      setError(err.message || "Failed to load drafts");
      setDrafts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Auto-save draft with debounce
   */
  const saveDraft = useCallback(async (draftData, immediate = false) => {
    // Clear existing timeout
    if (autoSaveTimeout.current) {
      clearTimeout(autoSaveTimeout.current);
    }

    const performSave = async () => {
      try {
        setIsSaving(true);

        const result = await saveDraft(draftData);

        console.log("Draft auto-saved:", new Date().toLocaleTimeString());
        return result;
      } catch (err) {
        console.error("Error auto-saving draft:", err);
        // Don't throw error for auto-save failures
      } finally {
        setIsSaving(false);
      }
    };

    if (immediate) {
      // Save immediately (e.g., when navigating away)
      return performSave();
    } else {
      // Debounced save (1 second delay)
      return new Promise((resolve) => {
        autoSaveTimeout.current = setTimeout(async () => {
          const result = await performSave();
          resolve(result);
        }, 1000);
      });
    }
  }, []);

  /**
   * Delete a draft
   */
  const removeDraft = useCallback(async (draftKey) => {
    try {
      const draftId = draftKey.split("_")[1]; // Extract ID from key

      await deleteDraft(draftId);

      // Update local state
      setDrafts((prev) => prev.filter((draft) => draft.key !== draftKey));

      return { success: true };
    } catch (err) {
      console.error("Error deleting draft:", err);
      throw err;
    }
  }, []);

  /**
   * Clear auto-save timeout on unmount
   */
  const clearAutoSave = useCallback(() => {
    if (autoSaveTimeout.current) {
      clearTimeout(autoSaveTimeout.current);
    }
  }, []);

  return {
    drafts,
    loading,
    error,
    isSaving,
    loadDrafts,
    saveDraft,
    removeDraft,
    clearAutoSave,
  };
};

export default useDrafts;
